import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import axios from "axios";
import { CheckCircle2, Clock, MessageCircle } from "lucide-react";
import PageShell from "@/components/PageShell";
import { useCart } from "@/shop/CartContext";
import { inr } from "@/shop/products";
import { API } from "@/shop/api";
import { trackPurchase } from "@/lib/pixel";

const ThankYou = () => {
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const { clear } = useCart();

  useEffect(() => {
    window.scrollTo(0, 0);
    clear();
    const run = async () => {
      try {
        const { data: o } = await axios.get(`${API}/orders/${orderId}`);
        setOrder(o);
        if (o.status === "paid" || o.status === "cod_pending") {
          trackPurchase(o);
        }
      } catch {
        setNotFound(true);
      }
    };
    run();
  }, [orderId]);

  const paid = order?.status === "paid";
  const cod = order?.status === "cod_pending";

  return (
    <PageShell>
      <Helmet>
        <title>Thank You | Zentra</title>
      </Helmet>
      <section className="px-5 py-16 lg:px-12" data-testid="thankyou-page">
        <div className="mx-auto max-w-[700px] text-center">
          {notFound ? (
            <h1 className="font-display text-4xl uppercase text-white">Order not found</h1>
          ) : !order ? (
            <p className="text-white/50">Loading your order…</p>
          ) : (
            <>
              {paid ? (
                <CheckCircle2 className="mx-auto h-12 w-12 text-[#7ED321]" />
              ) : (
                <Clock className="mx-auto h-12 w-12 text-[#7ED321]" />
              )}
              <h1 className="mt-5 font-display text-4xl uppercase leading-[0.95] text-white sm:text-5xl" data-testid="thankyou-heading">
                {paid ? "Payment received!" : cod ? "Order placed!" : "Thank you!"}
              </h1>
              <p className="mt-3 text-sm leading-relaxed text-[#A6A66A]">
                {paid
                  ? "Your payment is confirmed. We're packing your Zentra bars now."
                  : cod
                  ? "Your cash-on-delivery order is saved — we've opened WhatsApp so you can confirm it with our team."
                  : "We're reviewing your order status and will update you on WhatsApp."}
              </p>

              <div className="mt-10 rounded-2xl border border-white/10 bg-[#121212] p-6 text-left" data-testid="thankyou-order-card">
                <div className="flex items-center justify-between">
                  <span className="text-xs uppercase tracking-[0.18em] text-white/50">Order</span>
                  <span className="font-display text-2xl text-white" data-testid="thankyou-order-number">{order.order_number}</span>
                </div>
                <ul className="mt-4 space-y-2 text-sm">
                  {order.items.map((i) => (
                    <li key={i.sku} className="flex justify-between text-white/80">
                      <span>{i.title} × {i.qty}</span>
                      <span>{inr(i.price * i.qty)}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-4 flex justify-between border-t border-white/10 pt-4">
                  <span className="text-xs uppercase tracking-[0.18em] text-white/50">
                    {paid ? "Paid" : "Total"} · {order.payment_method === "cod" ? "Cash on Delivery" : "Online payment"}
                  </span>
                  <span className="font-display text-2xl text-[#7ED321]" data-testid="thankyou-total">{inr(order.total)}</span>
                </div>
                <p className="mt-4 text-xs leading-relaxed text-white/40">
                  Delivering to: {order.customer.name}, {order.customer.address}, {order.customer.pincode}
                </p>
              </div>

              <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:justify-center">
                <a
                  href={`https://wa.me/${process.env.REACT_APP_WHATSAPP_NUMBER}?text=${encodeURIComponent(`Hi Zentra! Checking on my order ${order.order_number}.`)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="thankyou-whatsapp-button"
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-[#25D366] px-8 py-4 text-sm font-bold uppercase tracking-[0.12em] text-white transition-colors hover:bg-[#2be374]"
                >
                  <MessageCircle className="h-4 w-4" />
                  Track on WhatsApp
                </a>
                <a
                  href="/shop"
                  data-testid="thankyou-shop-button"
                  className="inline-flex items-center justify-center gap-2 rounded-full border border-white/20 px-8 py-4 text-sm font-bold uppercase tracking-[0.12em] text-white transition-colors hover:border-[#7ED321] hover:text-[#7ED321]"
                >
                  Continue Shopping
                </a>
              </div>
              <p className="mt-6 text-xs leading-relaxed text-white/40">
                Tip: save your order number — you can check the live status anytime on our{" "}
                <a href="/track" data-testid="thankyou-track-link" className="font-medium text-[#7ED321] underline underline-offset-2">
                  Track Order page
                </a>
                .
              </p>
            </>
          )}
        </div>
      </section>
    </PageShell>
  );
};

export default ThankYou;
